'use client'

import type {
    ClientChromeCopy,
    ClientFooterCopy,
    FormatCopy,
    MenuCopy,
    MobileMenuCopy,
    MoreMenuCopy,
    NavCopy,
    OpenInAppCopy,
    SearchCopy,
    TosBannerCopy,
} from '@/app/(site)/chrome-copy'
import { createContext, useContext, type ReactNode } from 'react'

/**
 * Carries server-resolved UI strings to client components that sit too deep to
 * reach with props.
 *
 * The global nav is the case this exists for: it renders inside the search bar,
 * inside HeaderInit, several levels below the nearest Server Component. Where a
 * Server Component is the direct parent -- the search header, the GIF detail
 * sidebar -- pass the strings as props instead and leave this alone.
 */
type SiteChromeCopyContextValue = {
    copy: ClientChromeCopy
    showMenuLocaleSwitcher: boolean
}

const SiteChromeCopyContext = createContext<SiteChromeCopyContextValue | undefined>(undefined)

const useSiteChromeCopyContext = (): SiteChromeCopyContextValue => {
    const context = useContext(SiteChromeCopyContext)

    if (!context) {
        throw new Error('SiteChromeCopyProvider is required for site chrome copy hooks')
    }

    return context
}

export const useNavCopy = (): NavCopy => useSiteChromeCopyContext().copy.nav

export const useMenuCopy = (): MenuCopy => useSiteChromeCopyContext().copy.menus

export const useMoreMenuCopy = (): MoreMenuCopy => useSiteChromeCopyContext().copy.moreMenu

export const useMobileMenuCopy = (): MobileMenuCopy => useSiteChromeCopyContext().copy.mobileMenu

export const useFormatCopy = (): FormatCopy => useSiteChromeCopyContext().copy.formats

export const useMenuFooterCopy = (): ClientFooterCopy => useSiteChromeCopyContext().copy.footer

export const useShowMenuLocaleSwitcher = (): boolean => useSiteChromeCopyContext().showMenuLocaleSwitcher

export const useSearchCopy = (): SearchCopy => useSiteChromeCopyContext().copy.search

export const useTosBannerCopy = (): TosBannerCopy => useSiteChromeCopyContext().copy.tosBanner

export const useOpenInAppCopy = (): OpenInAppCopy => useSiteChromeCopyContext().copy.openInApp

export const SiteChromeCopyProvider = ({
    copy,
    showMenuLocaleSwitcher,
    children,
}: {
    copy: ClientChromeCopy
    showMenuLocaleSwitcher: boolean
    children: ReactNode
}) => (
    <SiteChromeCopyContext.Provider value={{ copy, showMenuLocaleSwitcher }}>{children}</SiteChromeCopyContext.Provider>
)
